/**
 * Spec C (C4) — insight-impact view: "did the insight change reach this
 * strategy, and does it matter?" Renders the C3 payload (content diff +
 * reverse insight→variable→block index + material/minor/none verdict) from
 * `GET /api/cli/v1/projects/:pid/strategies/:sid/insight-impact?from&to`.
 *
 * Mode 3 (dry-run, D52): the SAME report run before any regeneration. Per the
 * freeze-then-branch UX rule the reviewed-no-change offer renders AFTER the
 * report (verdict `none`), never as a gate — recording appends a
 * `feedbackReviews` note onto the TO analysis via the EXISTING
 * InsightsFirestoreService.update surface (no new write path).
 *
 * Dead-end check (L1): no predecessor analysis → an explanatory empty state
 * with the weight-edit/clone CTAs named, not an error.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { getAuth } from 'firebase/auth';
import type {
  CliInsightCitationRef,
  CliInsightImpactData,
  CliInsightRevisePlanData,
} from '@modules/shared-types';
import { InsightsFirestoreService } from '@modules/insights-analyzer/services/InsightsFirestoreService';
import { TrustChip } from '@modules/insights-analyzer/components/TrustChip';
import type { ThesisEvidence } from '@modules/insights-analyzer/utils/trustCopy';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { getInsightImpactApiBaseUrl } from './insightImpactApiBase';
import { useInsightEvidence } from './DataMappingViewerV2/useInsightEvidence';
import { UNKNOWN_ACTOR } from './DataMappingViewerV2/insightEvidence';

const VERDICT_PRESENTATION: Record<CliInsightImpactData['verdict'], { color: 'error' | 'warning' | 'success'; label: string; meaning: string }> = {
  material: {
    color: 'error',
    label: 'Material',
    meaning: 'A moved insight is cited before the value point — regeneration is warranted (Mode 1 or targeted Mode 2).',
  },
  minor: {
    color: 'warning',
    label: 'Minor',
    meaning: 'Moved insights are cited, but none critically — a targeted refresh (Mode 2) is enough when you act.',
  },
  none: {
    color: 'success',
    label: 'No impact',
    meaning: 'Nothing this strategy cites moved — no regeneration needed.',
  },
};

interface AnalysisOption {
  id: string;
  name: string;
  createdAt?: string;
  /** Spec D2 lineage root — pre-D2 docs fall back to their own id. */
  lineageId?: string;
  raw: Record<string, unknown>;
}

export interface InsightImpactDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  strategyId: string;
  /** Pinned analysis from the strategy inputManifest — the natural FROM. */
  defaultFromAnalysisId?: string;
  /**
   * Spec D4 (7b): deep-link pair (?impactFrom&impactTo from the experiment
   * card) — skips FROM=pin/TO=lineage-head auto-resolution entirely.
   */
  explicitPair?: { from: string; to: string };
  /**
   * Spec C (C6) guided chain: fields needed for a Mode 1 full revise of THIS
   * strategy (the generate endpoint requires them). Absent → Mode 1 hidden.
   */
  strategySnapshot?: {
    name?: string;
    description?: string;
    strategyType?: string;
    toneOfVoice?: string;
    designDescription?: string;
    sourceData?: Record<string, unknown>;
  };
}

export const InsightImpactDialog: React.FC<InsightImpactDialogProps> = ({
  open,
  onClose,
  projectId,
  strategyId,
  defaultFromAnalysisId,
  explicitPair,
  strategySnapshot,
}) => {
  const [analyses, setAnalyses] = useState<AnalysisOption[] | null>(null);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CliInsightImpactData | null>(null);
  const [reviewRecorded, setReviewRecorded] = useState(false);
  const [recordingReview, setRecordingReview] = useState(false);
  // Spec C (C6) guided chain — regeneration dispatch state (act-first: the
  // buttons ride the report, they never gate it).
  const [dispatching, setDispatching] = useState<'mode1' | 'mode2' | null>(null);
  const [dispatchNote, setDispatchNote] = useState<string | null>(null);
  // Spec D3 (AC-D3-5): the tree is the DEFAULT view; the raw pair picker is
  // demoted behind Advanced. No pin / no lineage mate → Advanced opens itself.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [autoComputed, setAutoComputed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setReviewRecorded(false);
    setAdvancedOpen(false);
    setAutoComputed(false);
    setDispatchNote(null);
    let cancelled = false;
    new InsightsFirestoreService(projectId).list({ limit: 50 })
      .then((docs) => {
        if (cancelled) return;
        const completed = (docs as Array<Record<string, unknown>>)
          .filter((doc) => doc.status === 'completed' || doc.status === 'partial')
          .map((doc) => ({
            id: String(doc.id),
            name: String(doc.name || doc.id),
            createdAt: typeof doc.createdAt === 'string' ? doc.createdAt : undefined,
            lineageId: typeof doc.lineageId === 'string' ? doc.lineageId : undefined,
            raw: doc,
          }))
          .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
        setAnalyses(completed);
        // Spec D4 (7b): a deep-linked pair is authoritative — no auto-resolution.
        if (explicitPair) {
          setFromId(explicitPair.from);
          setToId(explicitPair.to);
          return;
        }
        const pinned = defaultFromAnalysisId && completed.some((a) => a.id === defaultFromAnalysisId);
        const from = pinned ? defaultFromAnalysisId! : completed[1]?.id ?? '';
        // D3 auto-TO: newest completed doc sharing FROM's lineage chain (list is
        // newest-first). Falls back to "any other analysis" for pre-D2 docs.
        const fromOption = completed.find((a) => a.id === from);
        const fromLineage = fromOption?.lineageId ?? from;
        const lineageMate = completed.find((a) => a.id !== from && (a.lineageId ?? a.id) === fromLineage);
        const to = lineageMate?.id ?? completed.find((a) => a.id !== from)?.id ?? '';
        setFromId(from);
        setToId(to);
        // Degrade (AC-D3-5): no manifest pin or no lineage mate → open the
        // Advanced picker instead of auto-computing an arbitrary pair.
        if (!pinned || !lineageMate) setAdvancedOpen(true);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load analyses');
      });
    return () => { cancelled = true; };
  }, [open, projectId, defaultFromAnalysisId, explicitPair]);

  const compute = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setReviewRecorded(false);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error('User not authenticated');
      // D3 §9.3: only the frontend tree opts into content-plan consumers.
      const params = new URLSearchParams({ from: fromId, to: toId, consumers: '1' });
      const response = await fetch(
        `${getInsightImpactApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/insight-impact?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || payload?.detail || `HTTP ${response.status}`);
      }
      setResult(payload.data as CliInsightImpactData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impact computation failed');
    } finally {
      setLoading(false);
    }
  }, [projectId, strategyId, fromId, toId]);

  // D3 (AC-D3-5): single-flight auto-compute of the lineage pair on open.
  // Advanced-open on load means the pair was NOT auto-resolvable — no auto run.
  useEffect(() => {
    if (!open || autoComputed || advancedOpen || loading || result) return;
    if (!fromId || !toId || fromId === toId) return;
    setAutoComputed(true);
    void compute();
  }, [open, autoComputed, advancedOpen, loading, result, fromId, toId, compute]);

  // Spec D4 (7c): dialog path of the candidate conveyor — mints a discovery
  // experiment and prepares a corrected version on THIS exact pair; the hub
  // card is the review surface (the pipeline runs detached, the card polls).
  const [preparingCandidate, setPreparingCandidate] = useState(false);
  const dispatchPrepareVersion = useCallback(async () => {
    if (!result) return;
    setPreparingCandidate(true);
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error('User not authenticated');
      const response = await fetch(
        `${getInsightImpactApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/experiments/candidate/prepare`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            strategyId,
            fromAnalysisId: result.fromAnalysisId,
            toAnalysisId: result.toAnalysisId,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error?.message || payload?.detail || `HTTP ${response.status}`);
      const experimentId = (payload?.data as { experimentId?: string } | undefined)?.experimentId;
      setDispatchNote(`Preparing a corrected version — review and publish it from the Experiments hub${experimentId ? ` (experiment ${experimentId})` : ''}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Candidate preparation failed');
    } finally {
      setPreparingCandidate(false);
    }
  }, [projectId, strategyId, result]);

  // Spec C (C6) Mode 2: fetch the server plan, then dispatch ONE ordinary
  // block-update per affected block (the existing write surface) — unaffected
  // blocks never receive a call, which keeps them byte-stable.
  const dispatchMode2 = useCallback(async () => {
    if (!result) return;
    setDispatching('mode2');
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error('User not authenticated');
      const params = new URLSearchParams({ from: result.fromAnalysisId, to: result.toAnalysisId });
      const planResponse = await fetch(
        `${getInsightImpactApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/revise-plan?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const planPayload = await planResponse.json();
      if (!planResponse.ok) throw new Error(planPayload?.error?.message || `HTTP ${planResponse.status}`);
      const plan = planPayload.data as CliInsightRevisePlanData;
      if (plan.affectedBlocks.length === 0) {
        setDispatchNote('Nothing cites the change — run the Mode 3 report instead (no regeneration dispatched).');
        return;
      }
      const dispatched: string[] = [];
      for (const block of plan.affectedBlocks) {
        const response = await fetch(
          `${getInsightImpactApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/generate/block`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              strategyId,
              blockId: block.blockId,
              prompt: block.prompt,
              changeDiff: plan.changeDiff,
            }),
          },
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error?.message || `HTTP ${response.status} on block ${block.blockId}`);
        dispatched.push(block.blockName ?? block.blockId);
      }
      setDispatchNote(`Dispatched targeted regeneration for ${dispatched.length} block(s): ${dispatched.join(', ')}. Watch the task tray; compile when done (it auto-bakes the next version).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Targeted revise failed');
    } finally {
      setDispatching(null);
    }
  }, [projectId, strategyId, result]);

  // Spec C (C6) Mode 1: full revise of THIS strategy — the chain reopens a
  // baked head first (autoReopened echoed) and the prompts carry the diff.
  const dispatchMode1 = useCallback(async () => {
    if (!result || !strategySnapshot?.name || !strategySnapshot.strategyType || !strategySnapshot.sourceData) return;
    setDispatching('mode1');
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error('User not authenticated');
      const impactSummary = result.impactedInsights
        .map((impacted) => `[${impacted.status}] ${impacted.category}: ${impacted.canonicalDescription}`)
        .join('\n');
      const response = await fetch(
        `${getInsightImpactApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/generate/strategy`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            strategyId,
            name: strategySnapshot.name,
            description: strategySnapshot.description,
            strategyType: strategySnapshot.strategyType,
            toneOfVoice: strategySnapshot.toneOfVoice,
            designDescription: strategySnapshot.designDescription,
            sourceData: strategySnapshot.sourceData,
            reviseInPlace: true,
            changeDiff: `Insights moved between analysis ${result.fromAnalysisId} and ${result.toAnalysisId}:\n${impactSummary}`,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error?.message || `HTTP ${response.status}`);
      setDispatchNote(`Full revise dispatched (task ${payload.data?.taskId ?? payload.taskId ?? '—'})${payload.data?.autoReopened || payload.autoReopened ? ' — the baked head was reopened for editing first' : ''}. Compile when done (it auto-bakes the next version).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Full revise failed');
    } finally {
      setDispatching(null);
    }
  }, [projectId, strategyId, result, strategySnapshot]);

  // Mode 3 act-first offer: record "reviewed, no change needed" AFTER the
  // report — appends to feedbackReviews on the TO analysis (existing surface).
  const recordReviewedNoChange = useCallback(async () => {
    if (!result) return;
    setRecordingReview(true);
    try {
      const target = analyses?.find((analysis) => analysis.id === result.toAnalysisId);
      const existing = Array.isArray(target?.raw?.feedbackReviews)
        ? (target?.raw?.feedbackReviews as Array<Record<string, unknown>>)
        : [];
      await new InsightsFirestoreService(projectId).update(result.toAnalysisId, {
        feedbackReviews: [
          ...existing,
          {
            strategyId,
            fromAnalysisId: result.fromAnalysisId,
            verdict: 'none',
            reviewedAt: new Date().toISOString(),
          },
        ],
      } as never);
      setReviewRecorded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the review');
    } finally {
      setRecordingReview(false);
    }
  }, [analyses, projectId, result, strategyId]);

  const hasPredecessorChoice = (analyses?.length ?? 0) >= 2;
  const verdictPresentation = result ? VERDICT_PRESENTATION[result.verdict] : null;

  // D3 trust chips: per-thesis evidence from the TO analysis parent doc when
  // present (legacy inline insights). Subcollection-stored analyses carry no
  // parent-doc insights → chip degrades to ⚪ simulated until D5 fills realSignal.
  const evidenceByInsight = useMemo(() => {
    const map = new Map<string, ThesisEvidence>();
    const to = analyses?.find((analysis) => analysis.id === result?.toAnalysisId);
    const aggregated = to?.raw?.aggregatedInsights;
    if (aggregated && typeof aggregated === 'object') {
      for (const list of Object.values(aggregated as Record<string, unknown>)) {
        if (!Array.isArray(list)) continue;
        for (const insight of list) {
          if (insight && typeof insight === 'object' && typeof (insight as Record<string, unknown>).id === 'string') {
            const evidence = (insight as Record<string, unknown>).evidence;
            if (evidence && typeof evidence === 'object') {
              map.set((insight as Record<string, unknown>).id as string, evidence as ThesisEvidence);
            }
          }
        }
      }
    }
    return map;
  }, [analyses, result]);

  const analysisLabel = useCallback((analysisId: string): string => {
    const option = analyses?.find((analysis) => analysis.id === analysisId);
    return option ? `${option.name} · ${option.id.slice(0, 8)}` : analysisId.slice(0, 8);
  }, [analyses]);

  const citationSummary = useMemo(() => {
    if (!result) return new Map<string, string>();
    const summary = new Map<string, string>();
    for (const entry of result.index.entries) {
      const parts = entry.citations.slice(0, 3).map((citation) => {
        const where = citation.blockName ?? citation.blockId ?? 'unassigned';
        const via = citation.variableName ? `variable "${citation.variableName}"` : citation.screenId ? `screen ${citation.screenId}` : citation.via;
        return `${where} · ${via}`;
      });
      summary.set(entry.insightId, parts.join('  |  '));
    }
    return summary;
  }, [result]);

  /**
   * W13 DM2 (AC-W13-28): the QUOTE-level citations of each chain — the ones the
   * server index carries a `sourceItemId` for. One sub-row per quoted item (the
   * first citation of it wins; the same quote reused on two screens is still one
   * quote), so a chain whose screens cite only theses produces nothing here and
   * renders exactly as before.
   */
  const quoteCitationsByInsight = useMemo(() => {
    const byInsight = new Map<string, CliInsightCitationRef[]>();
    for (const entry of result?.index.entries ?? []) {
      const seen = new Set<string>();
      const quotes = entry.citations.filter((citation) => {
        if (!citation.sourceItemId || seen.has(citation.sourceItemId)) return false;
        seen.add(citation.sourceItemId);
        return true;
      });
      if (quotes.length > 0) byInsight.set(entry.insightId, quotes);
    }
    return byInsight;
  }, [result]);

  // The actor's name is display metadata the generator rarely emits, so the
  // pinned analysis fills it in — read only when a quote is actually shown.
  const quoteSourcePaths = useMemo(
    () => [...quoteCitationsByInsight.values()].flat().map((citation) => citation.sourcePath),
    [quoteCitationsByInsight],
  );
  const insightEvidence = useInsightEvidence({ projectId, sourcePaths: quoteSourcePaths });

  return (
    <SDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid={STRATEGY_LIST.insightImpactDialog}
      title={`Insight impact — ${strategyId}`}
    >
      <Box sx={{ px: 3 }}>
        {analyses === null && !error && (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={28} /></Stack>
        )}
        {analyses !== null && !hasPredecessorChoice && (
          <Alert severity="info" data-testid="insight-impact-empty">
            No earlier analysis to compare — this project has {analyses.length === 0 ? 'no completed analyses' : 'a single analysis'}.
            Edit actor weights and regenerate, or start a new analysis version (Insights tab) to create a comparable snapshot first.
          </Alert>
        )}
        {analyses !== null && hasPredecessorChoice && (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {/* D3 (AC-D3-5): comparing line = default view; picker demoted. */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" data-testid="insight-impact-comparing">
                Comparing <b>{fromId ? analysisLabel(fromId) : '—'}</b> → <b>{toId ? analysisLabel(toId) : '—'}</b>
              </Typography>
              {loading && <CircularProgress size={16} />}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                onClick={() => setAdvancedOpen((prev) => !prev)}
                data-testid="insight-impact-advanced"
              >
                Advanced {advancedOpen ? '▴' : '▾'}
              </Button>
            </Stack>

            {advancedOpen && (
              <Stack direction="row" spacing={1.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="From (earlier analysis)"
                  value={fromId}
                  onChange={(event) => setFromId(event.target.value)}
                  inputProps={{ 'data-testid': 'insight-impact-from' }}
                >
                  {analyses.map((analysis) => (
                    <MenuItem key={analysis.id} value={analysis.id}>{analysis.name} · {analysis.id.slice(0, 8)}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="To (current)"
                  value={toId}
                  onChange={(event) => setToId(event.target.value)}
                  inputProps={{ 'data-testid': 'insight-impact-to' }}
                >
                  {analyses.map((analysis) => (
                    <MenuItem key={analysis.id} value={analysis.id}>{analysis.name} · {analysis.id.slice(0, 8)}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  disabled={!fromId || !toId || fromId === toId || loading}
                  onClick={compute}
                  data-testid="insight-impact-compute"
                  sx={{ minWidth: 110 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Compute'}
                </Button>
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {result && verdictPresentation && (
              <Box data-testid="insight-impact-report">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={verdictPresentation.label}
                    color={verdictPresentation.color}
                    data-testid="insight-impact-verdict"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{verdictPresentation.meaning}</Typography>
                  {/* Spec D4 (7c): candidate conveyor, dialog path — review lands on the hub card. */}
                  <span title="Prepares a corrected version from this exact change; review and publish it from the Experiments hub">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={preparingCandidate}
                      onClick={dispatchPrepareVersion}
                      data-testid="insight-impact-prepare-version"
                    >
                      Prepare version
                    </Button>
                  </span>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {result.diff.entries.length} moved · {result.diff.unchangedCount} unchanged ·{' '}
                  {result.index.entries.length} cited insight(s) in this strategy
                  {result.index.valuePointBlockId ? ` · value point: ${result.index.valuePointBlockId}` : ' · no native paywall block (nothing critical)'}
                </Typography>

                {result.impactedInsights.length > 0 && (
                  <Box sx={{ mt: 1, pl: 1.5, borderLeft: theme => `2px solid ${theme.palette.divider}` }} data-testid="insight-impact-branch-strategy">
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Strategy</Typography>
                    <Typography variant="caption" color="text.secondary"> — impacted chains (insight → variable/screen → block)</Typography>
                    {result.impactedInsights.map((impacted) => (
                      <Box key={impacted.insightId} sx={{ mt: 0.75 }} data-testid={STRATEGY_LIST.insightImpactRow(impacted.insightId)}>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                          <Chip size="small" variant="outlined" label={impacted.status} color={impacted.status === 'removed' ? 'error' : impacted.status === 'appeared' ? 'success' : 'default'} />
                          {impacted.critical && <Chip size="small" color="error" label="critical" />}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{impacted.canonicalDescription}</Typography>
                          <TrustChip
                            evidence={evidenceByInsight.get(impacted.insightId)}
                            testId={`insight-trust-chip-${impacted.insightId}`}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                          {impacted.category} · {impacted.citationCount} citation(s)
                          {citationSummary.get(impacted.insightId) ? ` — ${citationSummary.get(impacted.insightId)}` : ''}
                        </Typography>
                        {/* W13 DM2: the actor quote a screen cited, under the thesis it belongs to. */}
                        {(quoteCitationsByInsight.get(impacted.insightId) ?? []).map((citation) => {
                          const itemId = citation.sourceItemId as string;
                          const variant = citation.analysisId
                            ? insightEvidence.byAnalysis[citation.analysisId]?.[impacted.insightId]?.quotes?.[itemId]
                            : undefined;
                          const quoted = citation.quotedValue || variant?.phrase;
                          const actor = citation.quoteActorName || variant?.actorName || UNKNOWN_ACTOR;
                          return (
                            <Box
                              key={itemId}
                              data-testid={STRATEGY_LIST.insightImpactQuote(impacted.insightId, itemId)}
                              sx={{ mt: 0.5, ml: 0.5, pl: 1, borderLeft: theme => `2px solid ${theme.palette.divider}` }}
                            >
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {quoted ? `“${quoted}”` : itemId}
                                <Box component="span" sx={{ color: 'text.secondary' }}>{` — ${actor}`}</Box>
                              </Typography>
                              {citation.quoteJustification && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {citation.quoteJustification}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                )}

                {result.verdict !== 'none' && (
                  <Box sx={{ mt: 1, pl: 1.5, borderLeft: theme => `2px solid ${theme.palette.divider}` }} data-testid="insight-impact-branch-onboardings">
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Onboardings</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Funnels built from this strategy pick the changes up on the next compile + publish — republish after revising.
                    </Typography>
                  </Box>
                )}

                {/* D3 §9.3: content plan is INFO ONLY — never part of the verdict. */}
                {result.contentPlan && (result.contentPlan.contentStrategies.length > 0 || result.contentPlan.topicBacklog.length > 0) && (
                  <Box sx={{ mt: 1, pl: 1.5, borderLeft: theme => `2px solid ${theme.palette.divider}` }} data-testid="insight-impact-branch-content-plan">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Content plan</Typography>
                      <Chip size="small" variant="outlined" label="info only" />
                    </Stack>
                    {result.contentPlan.contentStrategies.map((consumer) => (
                      <Typography key={consumer.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Content strategy “{consumer.title ?? consumer.id}” pins this analysis. Informational — content is not blocked by this change.
                      </Typography>
                    ))}
                    {result.contentPlan.topicBacklog.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {result.contentPlan.topicBacklog.length} backlog topic(s) pin it:{' '}
                        {result.contentPlan.topicBacklog.slice(0, 5).map((topic) => topic.title ?? topic.id).join(', ')}
                        {result.contentPlan.topicBacklog.length > 5 ? '…' : ''}
                      </Typography>
                    )}
                  </Box>
                )}

                {result.losses.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }} data-testid="insight-impact-losses">
                    {result.losses.length} citation(s) could not be resolved (kept as notes, nothing was dropped silently).
                  </Alert>
                )}

                {/* Spec C (C6) guided chain: act-first regeneration buttons
                    ride the report when moved insights ARE cited. */}
                {result.verdict !== 'none' && !dispatchNote && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={dispatching !== null}
                      onClick={dispatchMode2}
                      data-testid="insight-impact-mode2"
                    >
                      {dispatching === 'mode2' ? <CircularProgress size={18} /> : 'Revise affected blocks (Mode 2)'}
                    </Button>
                    {strategySnapshot?.name && strategySnapshot.strategyType && strategySnapshot.sourceData && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={dispatching !== null}
                        onClick={dispatchMode1}
                        data-testid="insight-impact-mode1"
                      >
                        {dispatching === 'mode1' ? <CircularProgress size={18} /> : 'Full revise (Mode 1)'}
                      </Button>
                    )}
                  </Stack>
                )}
                {dispatchNote && (
                  <Alert severity="info" sx={{ mt: 1 }} data-testid="insight-impact-dispatch-note">
                    {dispatchNote}
                  </Alert>
                )}

                {result.verdict === 'none' && (
                  <Alert
                    severity="success"
                    sx={{ mt: 1 }}
                    action={reviewRecorded ? undefined : (
                      <Button
                        size="small"
                        disabled={recordingReview}
                        onClick={recordReviewedNoChange}
                        data-testid="insight-impact-record-review"
                      >
                        Record reviewed-no-change
                      </Button>
                    )}
                    data-testid="insight-impact-none-offer"
                  >
                    {reviewRecorded
                      ? 'Recorded: reviewed, no regeneration needed.'
                      : 'Nothing this strategy cites moved. You can record that this change was reviewed with no action needed.'}
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </SDialog>
  );
};
